import React from "react";
import { useI18n } from "react-simple-i18n";
import { alpha, styled } from "@mui/material/styles";
import { Box, Typography, Grid, Paper } from "@mui/material";

import StarIcon from '@mui/icons-material/Star';
import GroupsIcon from '@mui/icons-material/Groups';
import TimelineIcon from '@mui/icons-material/Timeline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CloseIcon from '@mui/icons-material/Close';

import { instructorRecruitmentImg } from "../assets/image";
import { GlobalSpacing } from "../components/layout/styles";
import { FirstButton } from "../components/buttons/buttons";
import { useCustomNavigate } from "../provider/navigate";
import { useGlobalContext } from "../provider";

const benefits = [
	{
		icon: <TimelineIcon />,
		title: "Simple Process",
		jpTitle: "簡単なプロセス",
		description: "Just upload your educational MP4 video and get rewarded",
		jpDescription: "教育用MP4動画をアップロードするだけで報酬が得られます"
	},
	{
		icon: <StarIcon />,
		title: "Friend Sponsorship",
		jpTitle: "フレンドスポンサーシップ",
		description: "Join our sponsorship program and earn rewards for your videos",
		jpDescription: "スポンサーシッププログラムに参加して動画の報酬を獲得"
	},
	{
		icon: <GroupsIcon />,
		title: "Easy Collaboration",
		jpTitle: "簡単な協力",
		description: "Simple video submission process with quick approval",
		jpDescription: "迅速な承認による簡単な動画提出プロセス"
	},
	{
		icon: <EmojiEventsIcon />,
		title: "Multiple Submissions",
		jpTitle: "複数の提出",
		description: "Sell multiple videos and increase your earnings",
		jpDescription: "複数の動画を販売して収益を増やせます"
	}
]

const requirements = [
	{
		title: "Video Requirements",
		jpTitle: "動画の要件",
		items: [
			"MP4 format, HD quality (1080p minimum)",
			"10-30 minutes in length",
			"Clear audio and professional presentation"
		],
		jpItems: [
			"MP4形式、HD品質（1080p以上）",
			"長さ10〜30分",
			"クリアな音声とプロフェッショナルな発表"
		]
	},
	{
		title: "Content Requirements",
		jpTitle: "コンテンツの要件",
		items: [
			"Educational content in your area of expertise",
			"Well-structured and engaging presentation",
			"Original content with clear explanations"
		],
		jpItems: [
			"専門分野における教育コンテンツ",
			"よく構成された魅力的なプレゼンテーション",
			"明確な説明による独自のコンテンツ"
		]
	},
	{
		title: "Submission Process",
		jpTitle: "提出プロセス",
		items: [
			"Submit video through our platform",
			"Include brief content description",
			"Payment processed after approval"
		],
		jpItems: [
			"プラットフォームを通じて動画を提出",
			"コンテンツの簡単な説明を含める",
			"承認後に報酬を支払い"
		]
	}
]

const Recruitment = () => {
	const { t } = useI18n();
	const { customNavigate } = useCustomNavigate();
	const [globalState, { dispatch }]: GlobalContextType = useGlobalContext();

	const applyNow = () => {
		customNavigate("/register");
		dispatch({ showRecruitment: false });
	}

	const onClose = () => {
		dispatch({ showRecruitment: false });
	}

	return (
		<React.Fragment>
			{(globalState.showRecruitment && !globalState.loginStatus && !globalState.loading) && (
				<RecruitmentWrapper>
					<CloseButton onClick={onClose}>
						<CloseIcon fontSize="large" />
					</CloseButton>

					{/* Hero Section */}
					<HeroSection>
						<HeroBackground />

						<HeroContainer>
							<Typography variant="h1">
								{t("recruitment.title")}
							</Typography>

							<Typography variant="h4">
								{t("recruitment.subTitle")}
							</Typography>

							<Typography variant="h6" component="p">
								{t("recruitment.description")}
							</Typography>

							<FirstButton onClick={applyNow}>
								<Typography variant="h6">{t("buttons.startJourney")}</Typography>
							</FirstButton>
						</HeroContainer>
					</HeroSection>

					<MainContent>
						<MainContainer>
							{/* Benefits Section */}
							<Typography variant="h2">
								{t("recruitment.whyChooseUs")}
							</Typography>

							<Grid container spacing={{ xs: 2, md: 3 }}>
								{benefits.map((benefit, index) => (
									<Grid item xs={12} md={6} lg={3} key={index}>
										<BenefitCard elevation={3}>
											<IconWrapper>
												{benefit.icon}
											</IconWrapper>

											<Typography variant="h6">
												{globalState.lang === "en" ? benefit.title : benefit.jpTitle}
											</Typography>

											<Typography variant="body2">
												{globalState.lang === 'en' ? benefit.description : benefit.jpDescription}
											</Typography>
										</BenefitCard>
									</Grid>
								))}
							</Grid>

							{/* Requirements Section */}
							<Typography variant="h2" sx={{ mt: 3 }}>
								{t("recruitment.lookingFor")}
							</Typography>

							<Grid container spacing={{ xs: 2, md: 3 }}>
								{requirements.map((category, index) => (
									<Grid item xs={12} md={4} key={index}>
										<RequirementCard elevation={3}>
											<Typography variant="h5">
												{globalState.lang === 'en' ? category.title : category.jpTitle}
											</Typography>

											<List>
												{(globalState.lang === 'en' ? category.items : category.jpItems).map((item, idx) => (
													<ListItem key={idx} typography="body1">• {item}</ListItem>
												))}
											</List>
										</RequirementCard>
									</Grid>
								))}
							</Grid>
						</MainContainer>
					</MainContent>
				</RecruitmentWrapper>
			)}
		</React.Fragment>
	)
}

const RecruitmentWrapper = styled(Box)(({ theme }) => ({
	top: 0, left: 0,
	position: "fixed",
	width: "100vw",
	height: "100vh",
	overflow: "auto",

	zIndex: 10,
	display: "flex",
	flexDirection: "column",
	background: theme.palette.common.mainBg,
}))

const HeroSection = styled(Box)({
	width: '100%',
	minHeight: '100vh',
	position: 'relative',
	display: 'flex',
	flexDirection: "column",
	justifyContent: "center",
	overflow: 'hidden',
	background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 50%, rgba(255,255,255,0.85) 100%)',
})

const HeroContainer = styled(GlobalSpacing)(({ theme }) => ({
	zIndex: 1,
	flex: 1,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	gap: theme.spacing(3),
	textAlign: "center",

	h1: {
		maxWidth: 900,
		fontWeight: 800,
		background: `linear-gradient(135deg, ${theme.palette.common.thirdColor} 0%, ${theme.palette.common.firstColor} 100%)`,
		WebkitBackgroundClip: 'text',
		WebkitTextFillColor: 'transparent',
		marginBottom: theme.spacing(2),
		lineHeight: 1.2,
		letterSpacing: '-0.02em',

		[theme.breakpoints.down('md')]: {
			fontSize: '2.8rem',
		}
	},

	h4: {
		maxWidth: 800,
		fontWeight: 600,
		color: theme.palette.common.thirdColor,
		marginBottom: theme.spacing(2),
		lineHeight: 1.4,
		textShadow: `0px 0px 1px ${theme.palette.common.white}`,

		[theme.breakpoints.down('md')]: {
			fontSize: '1.5rem',
		}
	},

	p: {
		maxWidth: 800,
		color: alpha(theme.palette.common.thirdColor, 0.85),
		textShadow: `0px 0px 1px ${theme.palette.common.firstColor}`,
		fontWeight: 500,
	},

	button: {
		width: "100%",
		maxWidth: 300,
		marginTop: theme.spacing(4),
		padding: theme.spacing(1.8),
		borderRadius: theme.spacing(3),
		transition: 'all 0.3s ease',

		'&:hover': {
			transform: 'scale(1.05)',
			boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
		},

		h6: {
			fontSize: '1.2rem',
			fontWeight: 600,
			letterSpacing: '0.5px',
		}
	}
}))

const HeroBackground = styled(Box)({
	left: 0, right: 0,
	top: 0, bottom: 0,
	position: 'fixed',
	zIndex: 0,
	width: '100vw',
	height: '100vh',
	backgroundSize: 'cover',
	backgroundPosition: 'center',
	backgroundRepeat: 'no-repeat',
	backgroundImage: `url(${instructorRecruitmentImg})`,
	filter: 'brightness(1.1) saturate(1.2)',
})

const MainContent = styled(Box)(({ theme }) => ({
	zIndex: 3,
	position: 'relative',
	borderRadius: theme.spacing(5, 5, 0, 0),
	backgroundColor: theme.palette.common.mainBg,
	padding: theme.spacing(10, 0),

	[theme.breakpoints.down("md")]: {
		padding: theme.spacing(5, 0),
	}
}))

const MainContainer = styled(GlobalSpacing)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(5),

	h2: {
		fontWeight: 700,
		textAlign: "center",
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(3),
	}
}))

const BenefitCard = styled(Paper)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	gap: theme.spacing(2),
	padding: theme.spacing(4, 4),
	height: '100%',
	textAlign: 'center',
	borderRadius: theme.spacing(2),
	transition: 'all 0.3s ease',
	cursor: "pointer",
	border: `1px solid ${alpha(theme.palette.common.firstColor, 0.1)}`,
	background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.95)} 0%, ${alpha(theme.palette.common.white, 0.98)} 100%)`,

	h6: {
		fontWeight: 700,
		color: theme.palette.common.firstColor,
	},

	p: {
		color: alpha(theme.palette.common.firstColor, 0.75),
		lineHeight: 1.6,
	},

	'&:hover': {
		transform: 'translateY(-8px)',
		boxShadow: `0 20px 30px ${alpha(theme.palette.common.firstColor, 0.1)}`,
	},

	[theme.breakpoints.down("md")]: {
		padding: theme.spacing(3),
	}
}))

const IconWrapper = styled(Box)(({ theme }) => ({
	width: 70,
	height: 70,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	marginBottom: theme.spacing(2),
	color: 'white',
	borderRadius: '50%',
	background: `linear-gradient(135deg, ${theme.palette.common.thirdColor} 0%, ${theme.palette.common.firstColor} 100%)`,
	boxShadow: `0 10px 20px ${alpha(theme.palette.common.firstColor, 0.2)}`,

	'& svg': {
		fontSize: 35,
	},
}))

const RequirementCard = styled(Paper)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(2),
	padding: theme.spacing(3, 4),

	height: '100%',
	borderRadius: theme.spacing(1),
	transition: 'transform 0.2s ease-in-out',

	h5: {
		fontWeight: 500,
		color: alpha(theme.palette.common.firstColor, 0.8),
	},

	'&:hover': {
		transform: 'translateY(-8px)',
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
		padding: theme.spacing(3),
	}
}))

const List = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(1),
}))

const ListItem = styled(Typography)(({ theme }) => ({
	fontSize: '1rem',
	color: alpha(theme.palette.common.firstColor, 0.75),
}))

const CloseButton = styled(Box)(({ theme }) => ({
	zIndex: 10,
	position: "fixed",
	top: 50, right: 50,
	cursor: "pointer",

	display: "flex",
	flexDirection: "column",

	svg: {
		color: theme.palette.common.white,
		filter: `drop-shadow(0px 0px 3px ${theme.palette.common.firstColor})`
	},

	[theme.breakpoints.down("lg")]: {
		top: 35,
		right: 35,
	},

	[theme.breakpoints.down("md")]: {
		top: 20,
		right: 20,
	}
}))

export { Recruitment };